import styled from 'styled-components'
import { themeGet } from 'styled-system'
import Container from 'components/Container'

export const TitleRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 1rem 1.25rem;
  padding-bottom: 1rem;
  margin-left: 1.25rem;
`

export const Title = styled.h4`
  color: ${themeGet('colors.darkBlue')};
  font-size: 2rem;
  font-family: ${themeGet('headerFontFamilySemibold')};
  font-weight: 600;
  letter-spacing: 0.0313rem;
  margin: 0;
`

const programSelectChevronSvg = encodeURIComponent(
  `<svg width="12" height="7" viewBox="0 0 12 7" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M11 1L6 6L1 1" stroke="#555555" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
  </svg>`,
)

export const ProgramVariantSelectRoot = styled.div`
  position: relative;
  display: inline-flex;
  max-width: 100%;
`

export const ProgramSelectTrigger = styled.button`
  appearance: none;
  align-items: center;
  background-color: #e5f3fd;
  border: 1px solid #e5f3fd;
  border-radius: 999px;
  color: #555555;
  cursor: pointer;
  display: inline-flex;
  font-family: ${themeGet('fontFamily')};
  font-size: 0.9375rem;
  font-weight: 600;
  gap: 0.5rem;
  line-height: 1.25;
  max-width: 100%;
  padding: 0.45rem 0.75rem 0.313rem 1rem;

  &:focus-visible {
    outline: 2px solid ${themeGet('colors.darkBlue')};
    outline-offset: 2px;
  }
`

export const ProgramSelectCaret = styled.span`
  display: inline-flex;
  flex-shrink: 0;
  width: 12px;
  height: 8px;
  background-image: url('data:image/svg+xml,${programSelectChevronSvg}');
  background-repeat: no-repeat;
  background-position: center;
  transition: transform 0.15s ease;
  transform: ${({ $open }) => ($open ? 'rotate(180deg)' : 'none')};
`

export const ProgramSelectDropdown = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  z-index: 100;
  display: flex;
  flex-direction: column;
  min-width: 100%;
  padding: 0.75rem 1rem 0.5rem;
  background-color: ${themeGet('colors.white')};
  border: 2px solid #eef0f2;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
`

export const ProgramSelectOptionInner = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 1.25rem;
`

export const ProgramSelectOption = styled.button`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  margin: 0;
  padding: 0.75rem 0;
  border: none;
  background: none;
  cursor: pointer;
  text-align: left;

  &:first-of-type {
    padding-top: 0;
  }

  &:last-of-type {
    padding-bottom: 0;
  }

  &:not(:last-of-type) {
    position: relative;
    border-bottom: none;
  }

  /* Full-width hairline: offsets dropdown horizontal padding (1rem) */
  &:not(:last-of-type)::after {
    content: '';
    position: absolute;
    left: -1rem;
    right: -1rem;
    bottom: 0;
    height: 1px;
    background-color: #eef0f2;
  }

  &:hover {
    opacity: 0.85;
  }

  &:focus-visible {
    outline: 2px solid ${themeGet('colors.darkBlue')};
    outline-offset: 2px;
    border-radius: 2px;
  }
`

export const ProgramSelectOptionLabel = styled.span`
  color: #555555;
  font-family: ${themeGet('fontFamily')};
  font-size: 1rem;
  font-weight: 600;
  line-height: 1.3125rem;
`

export const ProgramSelectCheckWrap = styled.span`
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 17px;
  height: 13px;
  color: ${themeGet('colors.darkBlue')};
`

export const Description = styled.div`
  margin-left: 1.25rem;
  padding-bottom: 1rem;
`

export const SitePickerContainer = styled(Container)`
  padding: 0 1.25rem 1.5rem;
`

export const UserCount = styled.span`
  display: flex;
  font-family: ${themeGet('fontFamilySemibold')};
  margin-left: 1.25rem;
`

export const TextLinkContainer = styled(Container).attrs(({ dataTestId }) => ({
  'data-testid': dataTestId,
}))`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  width: 100%;
  padding: 1.25rem 1.25rem 0.6875rem;
`

export const StyledTextLinkDashboard = styled.a.attrs(({ dataTestId }) => ({
  'data-testid': dataTestId,
}))`
  font-family: ${themeGet('fontFamily')};
  color: ${themeGet('colors.darkGray')};
  text-decoration: none;

  &:hover {
    color: ${themeGet('colors.secondStepBlue')};
  }
`

export const PageHeaderContainer = styled(Container)`
  padding: 0 1.25rem 1.5rem;
`

export const PageTitle = styled.h1`
  font-family: ${themeGet('fontFamilySemibold')};
  font-size: 1.5rem;
  color: ${themeGet('colors.darkGray')};
  margin: 0;
  padding: 0 0 1rem 0;
`

export const SiteInfoContainer = styled(Container)`
  padding: 0 1.25rem 1rem;
  background-color: ${themeGet('colors.lightGray')};
  border-radius: 0.25rem;
  margin: 0 1.25rem 1rem;
`

export const SiteInfoText = styled.p`
  font-family: ${themeGet('fontFamily')};
  font-size: 0.875rem;
  color: ${themeGet('colors.darkGray')};
  margin: 0;
`

export const TableContainer = styled(Container)`
  position: relative;
  overflow: visible;
  padding: 0;
  margin: 0 1.5rem 0.75rem;
  width: auto;
`

export const LoadingWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
`

export const NoDataContainer = styled(Container)`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
  padding: 2rem;
`

export const NoDataText = styled.p`
  font-family: ${themeGet('fontFamily')};
  font-size: 1rem;
  color: ${themeGet('colors.darkGray')};
  text-align: center;
  margin: 0;
`

export const StyledTextLink = styled.button.attrs(({ dataTestId }) => ({
  'data-testid': dataTestId,
  role: 'link',
  type: 'button',
}))`
  align-items: center;
  background-color: transparent;
  border: none;
  display: flex;
  font-family: ${themeGet('fontFamilySemibold')};
  font-size: 1rem;
  color: ${themeGet('colors.blue')};
  text-decoration: none;
  cursor: pointer;

  &:hover,
  &:hover svg {
    color: ${themeGet('colors.secondStepBlue')};
  }
`
